import {createContext, useCallback, useMemo, useState} from 'react';

export type CaptureDockValue = {
  container: HTMLElement | null;
  acquire: (className?: string) => void;
  release: (className?: string) => void;
};

export const CaptureDockContext = createContext<CaptureDockValue | null>(null);

export const CaptureDockProvider = CaptureDockContext.Provider;

const remove = (claims: string[], className: string) => {
  const at = claims.indexOf(className);
  return at < 0 ? claims : [...claims.slice(0, at), ...claims.slice(at + 1)];
};

export function useCaptureDock() {
  const [container, setContainer] = useState<HTMLElement | null>(null);
  // One entry per mounted CaptureDock, carrying the classes that dock asked
  // the strip itself to wear — a screen whose action is inline on desktop
  // docks with `lg:hidden` so the strip goes with it rather than staying
  // behind as an empty bar.
  const [claims, setClaims] = useState<string[]>([]);

  const acquire = useCallback(
    (className = '') => setClaims(current => [...current, className]),
    [],
  );
  const release = useCallback(
    (className = '') => setClaims(current => remove(current, className)),
    [],
  );

  const value = useMemo<CaptureDockValue>(
    () => ({container, acquire, release}),
    [container, acquire, release],
  );

  return {
    containerRef: setContainer,
    value,
    isOccupied: claims.length > 0,
    className: claims.join(' '),
  };
}
